import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Pagination,
  Container,
  Row,
  Col,
  Spinner,
  Modal,
  Badge,
} from "react-bootstrap";

const API_URL = "https://radheyelectrical.com/api/inquiries_api.php";
const RECORDS_PER_PAGE = 10; // Lowered for better visibility, change as needed

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      setInquiries(data.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteInquiry = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this inquiry?"))
      return;

    try {
      await fetch(`${API_URL}?id=${id}`, { method: "DELETE" });
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleCardClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowModal(true);
  };

  // Pagination Logic
  const indexOfLast = currentPage * RECORDS_PER_PAGE;
  const indexOfFirst = indexOfLast - RECORDS_PER_PAGE;
  const currentRecords = inquiries.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(inquiries.length / RECORDS_PER_PAGE);

  const renderPagination = () => {
    let items = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 2 && i <= currentPage + 2)
      ) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </Pagination.Item>
        );
      } else if (
        (i === currentPage - 3 && i > 1) ||
        (i === currentPage + 3 && i < totalPages)
      ) {
        items.push(<Pagination.Ellipsis key={i} disabled />);
      }
    }
    return items;
  };

  return (
    <Container className="py-5">
      <header className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-0">Inquiry Dashboard</h2>
          <p className="text-muted">Manage your customer requests</p>
        </div>
        <Button
          variant="outline-primary"
          onClick={fetchData}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh List"}
        </Button>
      </header>

      {loading ? (
        <div className="text-center my-5 py-5">
          <Spinner animation="grow" variant="primary" />
          <p className="mt-3 text-muted">Loading inquiries...</p>
        </div>
      ) : inquiries.length === 0 ? (
        <Card className="text-center p-5 border-dashed">
          <Card.Body>
            <h5 className="text-muted">No inquiries found.</h5>
          </Card.Body>
        </Card>
      ) : (
        <>
          {currentRecords.map((item) => (
            <Card
              key={item.id}
              className="mb-3 border-0 shadow-sm inquiry-card"
              onClick={() => handleCardClick(item)}
              style={{
                cursor: "pointer",
                transition: "transform 0.2s",
                borderLeft: "5px solid #0d6efd",
              }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={3}>
                    <div className="fw-bold fs-5 text-dark text-capitalize">
                      {item.name}
                    </div>
                    <div className="text-muted small">{item.phone}</div>
                  </Col>

                  <Col md={2}>
                    <Badge bg="light" text="dark" className="border px-3 py-2">
                      {item.service_required || "General"}
                    </Badge>
                  </Col>

                  <Col md={5}>
                    <div className="text-dark small mb-1">
                      <strong>Email:</strong> {item.email}
                    </div>
                    <div className="text-muted text-truncate small">
                      "{item.message}"
                    </div>
                  </Col>

                  <Col md={2} className="text-end">
                    <Button
                      variant="light"
                      className="text-danger"
                      size="sm"
                      onClick={(e) => deleteInquiry(e, item.id)}
                    >
                      Delete
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-light border-0 py-2 d-flex justify-content-between">
                <small className="text-muted">ID: #{item.id}</small>
                <small className="text-muted">
                  {new Date(item.created_at).toLocaleDateString()} at{" "}
                  {new Date(item.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </small>
              </Card.Footer>
            </Card>
          ))}

          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-5">
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {renderPagination()}
              <Pagination.Next
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Inquiry Details</Modal.Title>
        </Modal.Header>
        {selectedInquiry && (
          <Modal.Body className="pt-3">
            <Row className="mb-4">
              <Col sm={6}>
                <label className="text-uppercase small fw-bold text-muted d-block">
                  Client Name
                </label>
                <p className="fs-5">{selectedInquiry.name}</p>
              </Col>
              <Col sm={6}>
                <label className="text-uppercase small fw-bold text-muted d-block">
                  Service Requested
                </label>
                <Badge bg="primary">{selectedInquiry.service_required}</Badge>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col sm={6}>
                <label className="text-uppercase small fw-bold text-muted d-block">
                  Contact Details
                </label>
                <p className="mb-0">📞 {selectedInquiry.phone}</p>
                <p>📧 {selectedInquiry.email}</p>
              </Col>
              <Col sm={6}>
                <label className="text-uppercase small fw-bold text-muted d-block">
                  Submission Date
                </label>
                <p>{new Date(selectedInquiry.created_at).toLocaleString()}</p>
              </Col>
            </Row>

            <label className="text-uppercase small fw-bold text-muted d-block">
              Message
            </label>
            <div
              className="bg-light p-3 rounded border"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {selectedInquiry.message}
            </div>
          </Modal.Body>
        )}
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" href={`mailto:${selectedInquiry?.email}`}>
            Reply via Email
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .inquiry-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
        }
        .border-dashed {
          border: 2px dashed #dee2e6;
        }
      `}</style>
    </Container>
  );
}
